import {
    ActionTypeEnum,
    DailyTransactionTypeEnum,
    PaymentValueDetailsModel,
    RequestActionModel,
    RowStateEnum, TransactionActionOtherValueResponseModel,
    TransactionAddItemModel,
    TransactionDetailResponseModel,
    ValidateTransactionModeEnum
} from "../../../../../models";
import {
    deleteTransaction,
    getTransactionDetail,
    getTransactionDetailForRefund,
    saveTransaction,
    searchTransactions,
    validateTransactionEligibilityForPayment
} from "../../../../../serviceBroker/transactionApiServiceBroker";
import {generateGuid, getValueInCurrency, scrollToDocumentTop} from "../../../../../utils";
import {useNavigate} from "react-router-dom";
import {useTransactionActions} from "./useTransactionActions";
import {
    calculateTransactionTotalValues,
    clearTransactionDataForRefund,
    getAccountBalance,
    handleTransactionTotalValues,
    prepareAndValidateTransactionAsSourceRequest
} from "../../businessLogic/transactionBl";
import {validateGetRefundRequestDetails, validateTransactionEligibility} from "../../businessLogic/validationBl";
import {RoutePageConfigurations} from "../../../../../configuration";
import {useTransactionObject, useTransactionSearchRequest} from "./useTransactionSelectors";
import {transactionDetailInitialValues, transactionItemInitialValues} from "../../initialValues/initialValues";
import {useBeneficiaryList} from "../initializer/useTransactionInitializationSelectors";
import {extractTransactionParams} from "../../businessLogic/transactionUtilsBl";

export const useTransactionHandlers = (
    transactionType: number,
    formId: number
) => {
    //#region state & hooks
    const navigate = useNavigate();
    const searchRequest=  useTransactionSearchRequest();
    const { handleSetObject,handleSetResponseObject, handleSetLoading, handleSetValidationErrors ,handleSetToastModel,handleResetState,handleSetShowDeleteModal,handleSetPrintObject,handleSetList,handleSetSearchRequest,handleSetShowPaymentDetailsModel,handleSetTransactionItemObject,handleSetTransactionType,handleSetGuidKey} =useTransactionActions();
    const beneficiaryList=useBeneficiaryList();
    //#endregion
    //#region functions
    const handleClearControls = async ({
                                           refs,
                                           isResetRequestState = true,
                                       }: {
        refs?: Record<string, React.MutableRefObject<any>> | null;
        isResetRequestState?: boolean;
    }) => {
        if (refs && Object.keys(refs).length > 0) {
            Object.values(refs).forEach((ref) => {
                if (ref?.current?.clearValue) {
                    ref.current.clearValue();
                }
            });
        }
        if (isResetRequestState) {
            handleSetObject({ ...transactionDetailInitialValues });
        }
        handleSetTransactionItemObject({...transactionItemInitialValues});
    };
    const handleSaveTransactionDetails = async (
        request: TransactionDetailResponseModel
    ) => {
        handleSetLoading(true);
        const result = await saveTransaction(request);

        if (
            result.Errors !== null &&
            result.Errors !== undefined &&
            result.Errors.length !== 0
        ) {
           await handleAction({
                id: 0,
                action: ActionTypeEnum.RaiseError,
                request: result.Errors,
            });
        } else {
            await handleAction({
                action: ActionTypeEnum.AddSuccess,
            });
        }
        handleSetLoading(false);
    };
    const fillList=async ()=> {
        handleSetLoading(true);
        const result = await searchTransactions(searchRequest);
        handleSetList(result?.Result || []);
        handleSetLoading(false);
    };
    const fillData = async (searchParams: URLSearchParams) => {
        const transactionParams = extractTransactionParams(searchParams);
        if (transactionParams) {
            await handleAction(transactionParams);
        }
    };
    const handleAction = async (request: RequestActionModel) => {
        switch (request.action) {
            case ActionTypeEnum.AddSuccess:
            case ActionTypeEnum.DeleteSuccess:
            case ActionTypeEnum.SaveSuccess:
            case ActionTypeEnum.Success:
                scrollToDocumentTop();
                handleSetObject(transactionDetailInitialValues);
                handleSetResponseObject(null);
                handleSetToastModel({ show: true, variant: "success", body: "Operation successful", header: "Success" });
                handleSetValidationErrors([]);
                handleSetShowDeleteModal(false);
                handleSetSearchRequest({...searchRequest,transactionType:transactionType,searchGuid:generateGuid()});
                break;
            case ActionTypeEnum.Delete:
                handleSetLoading(true);
                const deleteTransactionsResponse = await deleteTransaction(request.request);
                if (deleteTransactionsResponse?.Errors && deleteTransactionsResponse.Errors.length > 0) {
                    handleSetValidationErrors(deleteTransactionsResponse.Errors);
                    handleSetShowDeleteModal(false)
                    scrollToDocumentTop();
                } else {
                    await handleAction({ action: ActionTypeEnum.DeleteSuccess });
                }
                handleSetLoading(false);
                break;
            case ActionTypeEnum.Add:
                handleSetObject(null);
                break;
            case ActionTypeEnum.View:
                handleSetLoading(true);
                handleSetValidationErrors([]);
                const viewRecord = await getTransactionDetail(request.request.id!);
                if (viewRecord?.Result) {
                    let  result = viewRecord.Result;
                    result=await calculateTransactionTotalValues(result)
                    result.rowState = RowStateEnum.View;
                    if (result.TransactionDetaillist?.length) {
                        result.TransactionDetaillist.forEach((row) => {
                            row.ItemDiscountInCurrency = row.ItemDiscount;
                            row.TotalInCurrency=getValueInCurrency(row.Total, row.ValueOfCurrency);
                            row.TotalTaxInCurrency=getValueInCurrency(row.TotalTax, row.ValueOfCurrency);
                            row.rowState = RowStateEnum.View;
                            row.rowKey = generateGuid();
                        });
                    }
                    handleSetObject(result);
                    //handleSetResetKey(resetKey+1);
                    handleSetGuidKey(generateGuid());
                }
                handleSetLoading(false);
                break;
            case ActionTypeEnum.Update:
            case ActionTypeEnum.ModifyStart:
                handleSetLoading(true);
                handleSetValidationErrors([]);
                scrollToDocumentTop();
                const modifyRecord = await getTransactionDetail(request.request.id!);
                if (modifyRecord?.Result) {
                    let result = modifyRecord.Result;
                    result=await calculateTransactionTotalValues(result)
                    result.rowState = RowStateEnum.Update;
                    if (result.TransactionDetaillist?.length) {
                        result.TransactionDetaillist.forEach((row) => {
                            row.ItemDiscountInCurrency = row.ItemDiscount;
                            row.TotalInCurrency=getValueInCurrency(row.Total, row.ValueOfCurrency);
                            row.TotalTaxInCurrency=getValueInCurrency(row.TotalTax, row.ValueOfCurrency);
                            row.rowState = RowStateEnum.Update;
                            row.rowKey = generateGuid();
                        });
                    }
                    const validateTransactionEligibilityErrors = await validateTransactionEligibility({
                        transactionId: result.ID,
                        creationDate: result.Date,
                        totalTax: result.TotalTax,
                        mode: ValidateTransactionModeEnum.Modify,
                    });
                    if (validateTransactionEligibilityErrors?.length > 0) {
                        handleSetValidationErrors(validateTransactionEligibilityErrors);
                        handleSetLoading(false);
                        return;
                    }
                    handleSetObject(result);
                }
                handleSetLoading(false);
                break;
            case ActionTypeEnum.AddItem:
                const addItemObject=request.request as TransactionAddItemModel;
                addItemObject.item.Transaction_Type = Number(transactionType);
                break;
            case ActionTypeEnum.TransactionRefundIInitialization:
                const refundInitParamUrl = `bImDq=${request.request.id!}&ojXNQ=${generateGuid()}`;
                switch (transactionType) {
                    case DailyTransactionTypeEnum.SalesInvoice:
                    case DailyTransactionTypeEnum.POSSales:
                    case DailyTransactionTypeEnum.POSSalesRefund:
                    case DailyTransactionTypeEnum.POSRestaurant:
                    case DailyTransactionTypeEnum.Reserve_Restaurant_Bill:
                    case DailyTransactionTypeEnum.ReturnPOSRestaurant:
                        navigate(`${RoutePageConfigurations.sales.salesRefundPage}?${refundInitParamUrl}`);
                        break;
                    case DailyTransactionTypeEnum.PurchasesInvoice:
                        navigate(`${RoutePageConfigurations.purchases.purchasesRefundPage}?${refundInitParamUrl}`);
                        break;
                }
                break;
            case ActionTypeEnum.TransactionRefund:
                handleSetLoading(true);
                await handleClearControls({isResetRequestState:false, refs: (request.otherValue as TransactionActionOtherValueResponseModel)?.refs ?? null});
                const modifyObject = await getTransactionDetail(request.id!);
                const modifyTransactionObject=modifyObject?.Result
                if(modifyTransactionObject == null){return }
                handleSetTransactionType(modifyTransactionObject?.TransactionType);
                const balance: number = await getAccountBalance(
                    modifyTransactionObject.Customer_ID !== null &&
                    modifyTransactionObject.Customer_ID !== undefined
                        ? modifyTransactionObject.Customer_ID
                        : 0,
                    modifyTransactionObject.Currency_ID !== null &&
                    modifyTransactionObject.Currency_ID !== undefined
                        ? modifyTransactionObject.Currency_ID
                        : null,
                    beneficiaryList
                );
                const dataObject = handleTransactionTotalValues(
                    modifyTransactionObject
                );
                handleSetObject({
                    ...dataObject,
                    balance: balance,
                    TransactionDetaillist: [...dataObject.TransactionDetaillist],
                });
                handleSetTransactionType(modifyTransactionObject.TransactionType);
                handleSetGuidKey(generateGuid());
                break;
            case ActionTypeEnum.IssueSaleTransactionFromSalePriceInitialization:
                const reSaleTransInitParamUrl = `rUHaU=${request.request.id!}&ojXNQ=${generateGuid()}`;
                navigate(`${RoutePageConfigurations.sales.salesInvoicePage}?${reSaleTransInitParamUrl}`);
                break;
            case ActionTypeEnum.IssueSaleTransactionFromSalePrice:
                handleSetLoading(true);
                handleSetValidationErrors([]);
                scrollToDocumentTop();
                const [recordForSale, errorsForSale] = await prepareAndValidateTransactionAsSourceRequest({
                    transactionId: request.id!,
                    actionType: request.action,
                    transactionType: transactionType,
                });
                if (errorsForSale?.length > 0) {
                    handleSetValidationErrors(errorsForSale);
                    handleSetObject(null);
                } else {
                    handleSetObject(recordForSale);
                }
                handleSetLoading(false);
                break;
            case ActionTypeEnum.InitializeCustomerPayment:
                handleSetLoading(true);
                handleSetValidationErrors([]);
                scrollToDocumentTop();
                const paymentEligibilityValidationResponse = await validateTransactionEligibilityForPayment(
                    Number(request.request.id!)
                );
                if (
                    paymentEligibilityValidationResponse?.Errors &&
                    paymentEligibilityValidationResponse.Errors.length > 0
                ) {
                    handleSetValidationErrors(paymentEligibilityValidationResponse.Errors);
                    return;
                }
                const payTransactionInitParamUrl = `K8VaI=${request.request.id!}&ojXNQ=${generateGuid()}`;
                navigate(`${RoutePageConfigurations.customers.customerPaymentPage}?${payTransactionInitParamUrl}`, {
                    state: paymentEligibilityValidationResponse.Result,
                });
                handleSetLoading(false);
                break;
            case ActionTypeEnum.DeleteStart:
                handleSetResponseObject(request.request);
                handleSetShowDeleteModal(true);
                break;
            case ActionTypeEnum.DeleteFailed:
                handleSetValidationErrors(request.request);
                handleSetShowDeleteModal(false);
                scrollToDocumentTop();
                break;
            case ActionTypeEnum.DeleteReset:
                handleSetValidationErrors([]);
                handleSetResponseObject(null);
                handleSetShowDeleteModal(false);
                break;
            case ActionTypeEnum.ClearError:
                handleSetValidationErrors([]);
                break;
            case ActionTypeEnum.Clear:
                scrollToDocumentTop();
                handleSetObject(transactionDetailInitialValues);
                handleResetState();
                handleSetValidationErrors([]);
                break;
            case ActionTypeEnum.RaiseError:
                scrollToDocumentTop();
                handleSetValidationErrors(request.request);
                break;
            case ActionTypeEnum.PrintPdf:
                handleSetPrintObject({id:request?.request?.id || 0,show:true});
                break;
            case ActionTypeEnum.AddPaymentValuesSuccess:
                const object = useTransactionObject();
                handleSetShowPaymentDetailsModel(false);
                const paymentDetails: PaymentValueDetailsModel =
                    request.request as PaymentValueDetailsModel;
                let dataObjet: TransactionDetailResponseModel = {
                    ...object!,
                    CashMoney: paymentDetails.cash,
                    CardMoney: paymentDetails.credit,
                    DelayMoney: paymentDetails.delay,
                    OthersMoney: paymentDetails.voucher,
                    Voucher_ID: paymentDetails.voucherId,
                    //CreditValue
                };
                handleSetObject(dataObjet);
                await handleSaveTransactionDetails(dataObjet);
                break;
            case ActionTypeEnum.AddPaymentValuesClear:
                break;
            case ActionTypeEnum.other:// ActionTypeEnum.TransactionRefund:
                handleSetLoading(true);
                handleSetValidationErrors([]);
                scrollToDocumentTop();
                let recordForReturn = await getTransactionDetailForRefund(request.id!);
                const refundErrors = await validateGetRefundRequestDetails(recordForReturn);
                if (refundErrors.length > 0) {
                    handleSetValidationErrors(refundErrors);
                    handleSetLoading(false);
                    return;
                }
                const refundRecord = await clearTransactionDataForRefund(
                    recordForReturn?.Result || transactionDetailInitialValues
                );
                handleSetObject(refundRecord);
                handleSetLoading(false);
                break;
            default:
                console.error(`Unhandled action: ${request.action}`);
                break;
        }
    };
    //#endregion
    return {
        fillData,
        fillList,
        handleAction,
    };
};